import {
  transformFiltersToPreferences,
  type Filters,
} from "@superbright/indexeddb-orm";

// Filter keys that round-trip through the URL.
export const FILTER_URL_KEYS = [
  "qty_bedrooms",
  "base_price",
  "date_availability",
  "highlights",
] as const;

type FilterUrlKey = (typeof FILTER_URL_KEYS)[number];

const NUMBER_KEYS: ReadonlySet<FilterUrlKey> = new Set(["base_price"]);
const NUMBER_ARRAY_KEYS: ReadonlySet<FilterUrlKey> = new Set(["qty_bedrooms"]);

const toRawValues = (val: unknown): (string | number)[] => {
  const arr = Array.isArray(val) ? val : [val];
  return arr
    .map((item) => {
      if (item == null) return undefined;
      if (typeof item === "object" && "value" in item) {
        return (item as { value: unknown }).value as string | number;
      }
      return item as string | number;
    })
    .filter(
      (v): v is string | number =>
        v != null && !(typeof v === "string" && v === "")
    );
};

/** Returns true if the URL has any filter param we care about. */
export const hasFilterParams = (search: URLSearchParams): boolean =>
  FILTER_URL_KEYS.some((k) => {
    const v = search.get(k);
    return v != null && v !== "";
  });

/** Serialize store filters into URL-friendly search params (filter keys only). */
export const filtersToSearchParams = (
  filters: Partial<Filters>
): URLSearchParams => {
  const params = new URLSearchParams();
  const prefs = transformFiltersToPreferences(
    filters as Record<string, unknown>
  );

  FILTER_URL_KEYS.forEach((key) => {
    if (key === "highlights") {
      const tags = prefs.highlights;
      if (tags?.length) params.set(key, tags.join(","));
      return;
    }

    const raw = (filters as Record<string, unknown>)[key];
    if (raw == null) return;

    if (NUMBER_KEYS.has(key)) {
      let candidate: unknown = raw;
      if (typeof raw === "object" && raw != null && "value" in raw) {
        candidate = (raw as { value: unknown }).value;
      }
      const n = Number(candidate);
      if (Number.isFinite(n)) params.set(key, String(n));
      return;
    }

    const vals = toRawValues(raw);
    if (vals.length) params.set(key, vals.join(","));
  });
  return params;
};

/** Parse URL search params into a partial filters object. */
export const searchParamsToFilters = (
  search: URLSearchParams
): Partial<Filters> => {
  const out: Record<string, unknown> = {};
  FILTER_URL_KEYS.forEach((key) => {
    const raw = search.get(key);
    if (raw == null || raw === "") return;

    if (NUMBER_KEYS.has(key)) {
      const n = Number(raw);
      if (Number.isFinite(n)) out[key] = n;
      return;
    }

    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (NUMBER_ARRAY_KEYS.has(key)) {
      const nums = parts
        .map((p) => Number(p))
        .filter((n) => Number.isFinite(n));
      if (nums.length) out[key] = nums;
    } else if (parts.length) {
      out[key] = parts;
    }
  });
  return out as Partial<Filters>;
};
